import React, { useState } from 'react'
import { Input, Button } from '../'
import { useModalDialog } from '../../hooks'

const val = (e) => (e.target !== undefined ? e.target.value : e.value)

const LinkRow = ({ link, sourceLabels, onRemove }) => (
  <div className="link-list-row">
    <span className="link-icon">🔗</span>
    <a href={link.link_url} target="_blank" rel="noreferrer" className="link">{link.link_label || link.link_url}</a>
    {link.link_label &&
      <span className="link-url-note">{link.link_url}</span>
    }
    {link.link_source &&
      <span className="link-source-note">via {sourceLabels[link.link_source] || link.link_source}</span>
    }
    {onRemove &&
      <button className="button-icon" title="Ta bort" onClick={() => onRemove(link)}>✕</button>
    }
  </div>
)

// The popup opened by LinkList's edit icon. Owns its own copy of `links`,
// seeded from the snapshot at open time, and updates it locally on every
// add/remove — the modal's Content is a static closure (see the root
// CLAUDE.md / useModalDialog gotcha), so it never sees the host page's own
// re-fetch after each change; onAdd is expected to resolve with the created
// link (see call sites) so a freshly-added row can be removed again without
// closing the dialog first.
const LinkListEditor = ({ initialLinks, sourceLabels, onAdd, onRemove }) => {
  const [links, setLinks] = useState(initialLinks)
  const [newLink, setNewLink] = useState({ label: '', url: '' })

  const submitNew = () => {
    if (!newLink.url) return
    onAdd(newLink).then(created => setLinks(prev => [...prev, created]))
    setNewLink({ label: '', url: '' })
  }

  const remove = (link) => {
    onRemove(link).then(() => setLinks(prev => prev.filter(l => l !== link)))
  }

  return (
    <div className="LinkList LinkListEditor">
      {links.length === 0 &&
        <p className="empty-text">Inga länkar</p>
      }
      {links.map((link, i) => (
        <LinkRow key={link.person_link_id || link.company_link_id || i} link={link} sourceLabels={sourceLabels} onRemove={remove} />
      ))}
      <div className="link-list-new-row">
        <Input name="label" placeholder="Etikett" value={newLink.label} onChange={e => setNewLink({ ...newLink, label: val(e) })} />
        <Input name="url" placeholder="URL" value={newLink.url} onChange={e => setNewLink({ ...newLink, url: val(e) })} />
        <Button value="+ Lägg till länk" onClick={submitNew} disabled={!newLink.url} />
      </div>
    </div>
  )
}

// Read-only {label, url} list for network.person_link / network.company_link,
// shared by PersonDetail, CompanyDetail, and the Kunder client page. Editing
// (add/remove) happens in a popup behind the ✎ icon next to the section
// title, not inline — each change persists immediately (no separate save),
// the popup is just a workspace for making several changes without
// reopening it each time. `title`/`titleClassName` let each app keep its
// own section-heading style (e.g. network's uppercase muted vs. Kunder's
// cc-section-title) while still centralizing the icon placement here.
export const LinkList = ({ links = [], onAdd, onRemove, sourceLabels = {}, title = 'Länkar', titleClassName }) => {
  const { showModalDialog, hideModalDialog } = useModalDialog()

  const openEditor = () => {
    showModalDialog({
      title: 'Redigera länkar',
      Content: () => <LinkListEditor initialLinks={links} sourceLabels={sourceLabels} onAdd={onAdd} onRemove={onRemove} />,
      submitLabel: null,
      cancelLabel: 'Stäng',
      onCancel: hideModalDialog,
    })
  }

  return (
    <div className="LinkList">
      <div className="link-list-header">
        <h3 className={titleClassName}>{title}</h3>
        <button type="button" className="button-icon link-list-edit-button" title="Redigera länkar" onClick={openEditor}>✎</button>
      </div>
      {links.length === 0 &&
        <p className="empty-text">Inga länkar</p>
      }
      {links.map(link => (
        <LinkRow key={link.person_link_id || link.company_link_id} link={link} sourceLabels={sourceLabels} />
      ))}
    </div>
  )
}
